import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import authService from './authService';
import { configureApiAuth } from '../services/apiClient';
import tokenStore from '../services/tokenStore';
import appConfig from '../config/appConfig';
import { homeFor } from '../config/roles';
import { setSignedInUser } from '../components/shared/mockData';

const AuthContext = createContext(null);

const CHANNEL = 'pinparcel-auth';
const ACTIVITY_KEY = 'rb_last_activity';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

// Per-user data cached in the browser that must not survive sign-out.
function clearUserData() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('rb_') && !k.startsWith('rb_demo_'))
      .forEach((k) => sessionStorage.removeItem(k));
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* storage unavailable */
  }
}

const openChannel = () => {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
};

/**
 * status: 'loading' while an existing session is restored, then 'authenticated' or 'anonymous'.
 * reason: why the last session ended ('logout' | 'idle' | 'expired' | 'other-tab'), for the login page.
 */
export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null, reason: null });
  const channel = useRef(null);

  const endSession = useCallback((reason) => {
    tokenStore.clear();
    clearUserData();
    setState({ status: 'anonymous', user: null, reason });
  }, []);

  const startSession = useCallback((user) => {
    setSignedInUser(user); // before rendering, so mock screens never show another account's data
    setState({ status: 'authenticated', user, reason: null });
  }, []);

  // Restore an existing session (refresh cookie / demo session) once on load.
  useEffect(() => {
    let active = true;
    configureApiAuth({ refresh: authService.refresh, onUnauthorized: (reason) => active && endSession(reason) });
    authService
      .restore()
      .then((user) => active && startSession(user))
      .catch(() => active && setState({ status: 'anonymous', user: null, reason: null }));
    return () => {
      active = false;
    };
  }, [endSession, startSession]);

  // Keep other tabs in sync: signing out in one tab signs out all tabs.
  useEffect(() => {
    const ch = openChannel();
    channel.current = ch;
    if (!ch) return undefined;
    ch.onmessage = (e) => {
      if (e.data?.type === 'logout') endSession('other-tab');
      if (e.data?.type === 'login') authService.restore().then(startSession).catch(() => {});
    };
    return () => ch.close();
  }, [endSession, startSession]);

  const broadcast = useCallback((type) => {
    try {
      channel.current?.postMessage({ type });
    } catch {
      /* ignore */
    }
  }, []);

  // Refresh the access token shortly before it expires.
  useEffect(() => {
    if (state.status !== 'authenticated') return undefined;
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      const expiresAt = tokenStore.expiresAt();
      if (!expiresAt) return;
      const delay = Math.max(5000, expiresAt - Date.now() - appConfig.auth.refreshLeewaySeconds * 1000);
      timer = setTimeout(() => {
        authService.refresh().catch(() => endSession('expired'));
      }, delay);
    };
    schedule();
    const unsubscribe = tokenStore.subscribe(schedule);
    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [state.status, endSession]);

  // Sign out after a period of inactivity (activity in any tab counts).
  const logoutRef = useRef(null);
  useEffect(() => {
    const minutes = appConfig.auth.idleTimeoutMinutes;
    if (state.status !== 'authenticated' || !minutes) return undefined;
    const limit = minutes * 60 * 1000;
    let last = Date.now();
    const touch = () => {
      const now = Date.now();
      if (now - last < 5000) return; // throttle
      last = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {
        /* ignore */
      }
    };
    const lastActivity = () => {
      try {
        return Math.max(last, Number(localStorage.getItem(ACTIVITY_KEY)) || 0);
      } catch {
        return last;
      }
    };
    touch();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
    const check = setInterval(() => {
      if (Date.now() - lastActivity() >= limit) logoutRef.current?.('idle');
    }, Math.min(30000, limit / 4));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, touch));
      clearInterval(check);
    };
  }, [state.status]);

  const login = useCallback(async (credentials) => {
    const user = await authService.login(credentials);
    startSession(user);
    broadcast('login');
    return user;
  }, [startSession, broadcast]);

  const logout = useCallback(async (reason = 'logout') => {
    try {
      await authService.logout();
    } catch {
      /* server unreachable: still clear everything locally */
    }
    endSession(reason);
    broadcast('logout');
  }, [endSession, broadcast]);
  logoutRef.current = logout;

  const value = useMemo(() => {
    const { status, user, reason } = state;
    return {
      status,
      user,
      reason,
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'loading',
      home: user ? homeFor(user.role) : '/',
      hasRole: (...roles) => Boolean(user && roles.flat().includes(user.role)),
      // UI-level ownership check; the API must also check ownership on every request.
      isOwner: (ownerId) => Boolean(user && ownerId !== undefined && ownerId !== null && String(ownerId) === user.id),
      login,
      logout,
      register: authService.register,
      forgotPassword: authService.forgotPassword,
    };
  }, [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
